import { type ElementsState } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementId,
  type CanvasElementType,
  type PropsOf,
} from '@blaze-runtime/engine-domain';
import { getWrapperItems } from '@blaze-runtime/engine-domain';
import { type ColumnDataType } from '@blaze-shared/api-contracts-domain';
import {
  type CanvasElementDataProperties,
  type CanvasElementPropertiesWithData,
  type DataBindingSourceInfoColumn,
  type DataBindingSourceType,
  isBindingActive,
  isSourceBound,
} from '@blaze-shared/elements-domain';
import { findColumn } from '@blaze-shared/schema-domain';
import {
  type DatasetColumn,
  type DatasetLinkValue,
  type DatasetRow,
  ID_COLUMNS,
} from '@blaze-shared/schema-domain';
import { indexOfMaxValue, indexOfMinValue, isNullish } from '@blaze-shared/shared-util';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';

export const isListSourceBound = (data: CanvasElementDataProperties[] | undefined): boolean =>
  !!data && isSourceBound(data, 'optionsList');

export const getFirstOption = <T extends DatasetRow, K extends keyof T>(
  optionsList: T[] | undefined,
  valueField: K,
): T | undefined => {
  if (!optionsList?.length) {
    return undefined;
  }

  if (typeof optionsList[0][valueField] !== 'number') {
    return optionsList[0];
  }
  // NOTE get lowest value
  const idx = indexOfMinValue(optionsList, valueField);
  return optionsList[idx];
};

export const getLastOption = <T extends DatasetRow, K extends keyof T>(
  optionsList: T[] | undefined,
  valueField: K,
): T | undefined => {
  if (!optionsList?.length) {
    return undefined;
  }
  const lastIdx = optionsList.length - 1;

  if (typeof optionsList[lastIdx][valueField] !== 'number') {
    return optionsList[lastIdx];
  }
  // NOTE get highest value
  const idx = indexOfMaxValue(optionsList, valueField);
  return optionsList[idx];
};

export const determineIdColumnNameOfDatasource = (
  columns?: DataBindingSourceInfoColumn[],
): string | undefined => {
  if (!columns?.length) {
    return '';
  }

  const idColumn = columns.find((column) => column.isId);
  if (idColumn) {
    return idColumn.name;
  }

  return columns.find(({ name }) => ID_COLUMNS.includes(name))?.name ?? columns[0].name;
};

export function filterOptionsWithEmptyLabel<T extends Record<string, unknown>>(
  labelField = 'label',
) {
  return (element: T): boolean => {
    const value = element[labelField];
    return !isNullish(value) && value !== '';
  };
}

/**
 * Gets binding from element props.
 * @param props
 * @param bindingKey
 */
export const getBindingFromProps = (
  props: PropsOf<CanvasElementType>,
  bindingKey: string,
): CanvasElementDataProperties | undefined =>
  (props as CanvasElementPropertiesWithData)?.data?.find((binding) => binding.key === bindingKey);

/**
 * Gets type of data for given binding key.
 * @param props
 * @param bindingKey
 */
export const getBoundDataType = (
  props: PropsOf<CanvasElementType>,
  bindingKey: string,
): ColumnDataType | undefined => {
  const binding = getBindingFromProps(props, bindingKey);
  return binding?.dataSource?.columns?.[0]?.dataType;
};

/**
 * Gets source type for given binding key.
 * @param props
 * @param bindingKey
 */
export const getBoundSourceType = (
  props: PropsOf<CanvasElementType>,
  bindingKey: string,
): DataBindingSourceType | undefined => {
  const binding = getBindingFromProps(props, bindingKey);
  return binding?.dataSource?.sourceType;
};

export const getBoundDataSourceColumn = (
  props: PropsOf<CanvasElementType>,
  bindingKey: string,
): DatasetColumn | undefined => {
  const binding = getBindingFromProps(props, bindingKey);
  const columnKey = binding?.bindings?.[bindingKey];
  return findColumn(binding?.dataSource?.columns, columnKey);
};

/**
 * Serialize data for elements that can emit multiple values.
 * E.g. If element's bound data type is String then serialize
 * as array of comma separated string.
 * @param props
 * @param value
 * @param valueField
 * @param selectionBindingKey
 */
export function serializeBindingOutput(
  props: PropsOf<CanvasElementType>,
  value: DatasetRow[],
  valueField = 'value',
  labelField = 'name',
  selectionBindingKey = 'selected',
): string | any[] {
  const selectionBoundDataType = getBoundDataType(props, selectionBindingKey);

  if (selectionBoundDataType === 'String') {
    return value.map((v) => v[valueField]).join(', ');
  } else if (selectionBoundDataType === 'Link') {
    return value.map<DatasetLinkValue>((v) => ({ rowId: v[valueField], display: v[labelField] }));
  } else if (selectionBoundDataType === 'Tag') {
    return value.map((v) => v[valueField]);
  }
  return value.filter(Boolean);
}

/**
 * Deserialize data for elements that can emit multiple values.
 * Same use case like in above function but reversed.
 * @param props
 * @param value
 * @param optionsList
 * @param valueField
 * @param selectionBindingKey
 */
export function deserializeBindingInput(
  props: PropsOf<CanvasElementType>,
  value: DatasetRow[] | string[] | string,
  optionsList: DatasetRow[] = [],
  valueField = 'value',
  selectionBindingKey = 'selected',
): DatasetRow[] | string[] | string {
  const selectionBoundDataType = getBoundDataType(props, selectionBindingKey);

  if (typeof value === 'string' && selectionBoundDataType === 'String') {
    if (!valueField) {
      return [];
    }
    const selectedKeys = value
      .split(',')
      .filter((v) => !!v)
      .map((v) => v.trim());

    // Options are not ready yet - do not filter them.
    if (!optionsList.length) {
      return selectedKeys;
    }

    return optionsList.filter((option) =>
      selectedKeys.some((selectedKey) => option[valueField] === selectedKey),
    );
  }

  if (!Array.isArray(value)) {
    return [value];
  }

  return value;
}

/**
 * Check if the element in an automatically generated field of its parent form-like container
 */
export function isElementGeneratedFieldOfFormLike(
  state: Pick<ElementsState, 'entities'>,
  elementId: CanvasElementId,
): boolean {
  const element = state.entities[elementId];
  if (!element || !hasDataProps(element)) {
    return false;
  }

  const sourceFormLikeId = element.props.data.find(
    (binding: CanvasElementDataProperties) =>
      isBindingActive(binding) && binding.dataSource?.sourceType === 'formContainer',
  )?.dataSource?.id;

  if (!sourceFormLikeId) {
    return false;
  }

  const sourceFormLike = state.entities[sourceFormLikeId];
  const refs = getWrapperItems(getWrapperRefs(sourceFormLike));

  // If some ref of the parent form-like container points to the element,
  // it means the field was automatically generated.
  return refs.some((item) => item.elementId === elementId);
}
